/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { environment } from "../../../../environments/environment";
import { CallServiceConfig } from "../../models/ui/call-service-config.model";
import { ENDPOINT_GROUPS } from "./apis.constants";
import { AnomalyDetection } from "./mocks/anomaly-detection/anomaly-detection";
import { Calculator } from "./mocks/calculator/calculator";
import { DashboardAI } from "./mocks/dashboard-ai/dashboard-ai";
import { DashboardCio } from "./mocks/dashboard-cio/dashboard-cio";
import { DashboardCloud } from "./mocks/dashboard-cloud/dashboard-cloud";
import { DashboardComponentEmissions } from "./mocks/dashboard-component-emissions/dashboard-component-emissions";
import { DashboardConfig } from "./mocks/dashboard-config/dashboard-config";
import { DashboardDataCenter } from "./mocks/dashboard-data-center/dashboard-data-center";
import { DashboardDataCenters } from "./mocks/dashboard-data-centers/dashboard-data-centers";
import { DashboardEnergy } from "./mocks/dashboard-energy/dashboard-energy";
import { DashboardHardwareAssetsRealtime } from "./mocks/dashboard-hardware-assets-realtime/dashboard-hardware-assets-realtime";
import { DashboardHardwareAssets } from "./mocks/dashboard-hardware-assets/dashboard-hardware-assets";
import { DashboardImpact } from "./mocks/dashboard-impact/dashboard-impact";
import { DashboardSci } from "./mocks/dashboard-sci/dashboard-sci";
import { DashboardSoftwareAssets } from "./mocks/dashboard-software-assets/dashboard-software-assets";
import { DashboardSoftwareAssetsFilters } from "./mocks/dashboard-software-assets/dashboard-software-assets-filters";
import { Regions } from "./mocks/regions/regions";
import { ServiceNow } from "./mocks/service-now/service-now";
import { StrategicApplications } from "./mocks/strategic-applications/strategic-applications";
import { Users } from "./mocks/users/users";
import { WhatIfCarbonIntensity } from "./mocks/what-if-carbon-intensity/what-if-carbon-intensity";
import { WhatIfLayer } from "./mocks/what-if-layer/what-if-layer";
import { WhatIfScenario } from "./mocks/what-if-scenario/what-if-scenario";
import { WhatIfSimulation } from "./mocks/what-if-simulation/what-if-simulation";
import { WhatIf } from "./mocks/what-if/what-if";

export const APIS: CallServiceConfig = {
	moduleDefaults: {
		isMocked: environment.isMocked,
		delay: 1000
	},
	apis: {
		[ENDPOINT_GROUPS.users.name]: {
			[ENDPOINT_GROUPS.users.operations.getRoles.name]: {
				endpoint: ENDPOINT_GROUPS.users.operations.getRoles.endpoint,
				isMocked: false,
				mock: Users.getRoles
			},
			[ENDPOINT_GROUPS.users.operations.getUserInfo.name]: {
				endpoint: ENDPOINT_GROUPS.users.operations.getUserInfo.endpoint,
				isMocked: false,
				mock: Users.getUserInfo
			},
			[ENDPOINT_GROUPS.users.operations.getUsers.name]: {
				endpoint: ENDPOINT_GROUPS.users.operations.getUsers.endpoint,
				isMocked: false,
				mock: Users.getUsers
			},
			[ENDPOINT_GROUPS.users.operations.getUser.name]: {
				endpoint: ENDPOINT_GROUPS.users.operations.getUser.endpoint,
				isMocked: false,
				mock: Users.getUser
			},
			[ENDPOINT_GROUPS.users.operations.addUser.name]: {
				endpoint: ENDPOINT_GROUPS.users.operations.addUser.endpoint,
				isMocked: false,
				mock: Users.addUser
			},
			[ENDPOINT_GROUPS.users.operations.updateUser.name]: {
				endpoint: ENDPOINT_GROUPS.users.operations.updateUser.endpoint,
				isMocked: false,
				mock: Users.updateUser
			},
			[ENDPOINT_GROUPS.users.operations.deleteUser.name]: {
				endpoint: ENDPOINT_GROUPS.users.operations.deleteUser.endpoint,
				isMocked: false,
				mock: Users.deleteUser
			},
			[ENDPOINT_GROUPS.users.operations.logout.name]: {
				endpoint: ENDPOINT_GROUPS.users.operations.logout.endpoint,
				isMocked: false,
				mock: Users.logout
			},
			[ENDPOINT_GROUPS.users.operations.getUsersFunctionalUnits.name]: {
				endpoint: ENDPOINT_GROUPS.users.operations.getUsersFunctionalUnits.endpoint,
				isMocked: false,
				mock: Users.getUsersFunctionalUnits
			}
		},
		[ENDPOINT_GROUPS.strategicApplications.name]: {
			[ENDPOINT_GROUPS.strategicApplications.operations.getFiles.name]: {
				endpoint: ENDPOINT_GROUPS.strategicApplications.operations.getFiles.endpoint,
				isMocked: false,
				mock: StrategicApplications.getFiles
			},
			[ENDPOINT_GROUPS.strategicApplications.operations.uploadFile.name]: {
				endpoint: ENDPOINT_GROUPS.strategicApplications.operations.uploadFile.endpoint,
				isMocked: false,
				mock: StrategicApplications.uploadFile
			},
			[ENDPOINT_GROUPS.strategicApplications.operations.getFileStatus.name]: {
				endpoint: ENDPOINT_GROUPS.strategicApplications.operations.getFileStatus.endpoint,
				isMocked: false,
				mock: StrategicApplications.getFileStatus
			},
			[ENDPOINT_GROUPS.strategicApplications.operations.renameFile.name]: {
				endpoint: ENDPOINT_GROUPS.strategicApplications.operations.renameFile.endpoint,
				isMocked: false,
				mock: StrategicApplications.renameFile
			},
			[ENDPOINT_GROUPS.strategicApplications.operations.deleteFile.name]: {
				endpoint: ENDPOINT_GROUPS.strategicApplications.operations.deleteFile.endpoint,
				isMocked: false,
				mock: StrategicApplications.deleteFile
			},
			[ENDPOINT_GROUPS.strategicApplications.operations.exportFile.name]: {
				endpoint: ENDPOINT_GROUPS.strategicApplications.operations.exportFile.endpoint,
				isMocked: false,
				mock: StrategicApplications.exportFile
			},
			[ENDPOINT_GROUPS.strategicApplications.operations.downloadTemplate.name]: {
				endpoint:
					ENDPOINT_GROUPS.strategicApplications.operations.downloadTemplate.endpoint,
				isMocked: false,
				mock: StrategicApplications.downloadTemplate
			},
			[ENDPOINT_GROUPS.strategicApplications.operations.getGeneralInformation.name]: {
				endpoint:
					ENDPOINT_GROUPS.strategicApplications.operations.getGeneralInformation.endpoint,
				isMocked: false,
				mock: StrategicApplications.getGeneralInformation
			},
			[ENDPOINT_GROUPS.strategicApplications.operations.getEmissionApplications.name]: {
				endpoint:
					ENDPOINT_GROUPS.strategicApplications.operations.getEmissionApplications
						.endpoint,
				isMocked: false,
				mock: StrategicApplications.getEmissionApplications
			},
			[ENDPOINT_GROUPS.strategicApplications.operations.getEmissionByScope.name]: {
				endpoint:
					ENDPOINT_GROUPS.strategicApplications.operations.getEmissionByScope.endpoint,
				isMocked: false,
				mock: StrategicApplications.getEmissionByScope
			},
			[ENDPOINT_GROUPS.strategicApplications.operations.getEmissionByDeployment.name]: {
				endpoint:
					ENDPOINT_GROUPS.strategicApplications.operations.getEmissionByDeployment
						.endpoint,
				isMocked: false,
				mock: StrategicApplications.getEmissionByDeployment
			},
			[ENDPOINT_GROUPS.strategicApplications.operations.getTotalEmissionsOvertime.name]: {
				endpoint:
					ENDPOINT_GROUPS.strategicApplications.operations.getTotalEmissionsOvertime
						.endpoint,
				isMocked: false,
				mock: StrategicApplications.getTotalEmissionsOvertime
			},
			[ENDPOINT_GROUPS.strategicApplications.operations.getEnvironmentalImpactByApplication
				.name]: {
				endpoint:
					ENDPOINT_GROUPS.strategicApplications.operations
						.getEnvironmentalImpactByApplication.endpoint,
				isMocked: false,
				mock: StrategicApplications.getEnvironmentalImpactByApplication
			}
		},
		[ENDPOINT_GROUPS.dashboardServiceNow.name]: {
			[ENDPOINT_GROUPS.dashboardServiceNow.operations.getGeneralInformation.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardServiceNow.operations.getGeneralInformation.endpoint,
				isMocked: false,
				mock: ServiceNow.getGeneralInformation
			},
			[ENDPOINT_GROUPS.dashboardServiceNow.operations.getDevicesCategories.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardServiceNow.operations.getDevicesCategories.endpoint,
				isMocked: false,
				mock: ServiceNow.getDevicesCategories
			},
			[ENDPOINT_GROUPS.dashboardServiceNow.operations.getDeviceCarbonFootprint.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardServiceNow.operations.getDeviceCarbonFootprint
						.endpoint,
				isMocked: false,
				mock: ServiceNow.getDeviceCarbonFootprint
			},
			[ENDPOINT_GROUPS.dashboardServiceNow.operations.postScopeCarbonFootprintBar.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardServiceNow.operations.postScopeCarbonFootprintBar
						.endpoint,
				isMocked: false,
				mock: ServiceNow.postScopeCarbonFootprintBar
			},
			[ENDPOINT_GROUPS.dashboardServiceNow.operations.getScopeCarbonFootprintTable.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardServiceNow.operations.getScopeCarbonFootprintTable
						.endpoint,
				isMocked: false,
				mock: ServiceNow.getScopeCarbonFootprintTable
			},
			[ENDPOINT_GROUPS.dashboardServiceNow.operations.postManufacturerEmissionPie.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardServiceNow.operations.postManufacturerEmissionPie
						.endpoint,
				isMocked: false,
				mock: ServiceNow.postManufacturerEmissionPie
			},
			[ENDPOINT_GROUPS.dashboardServiceNow.operations.getDeviceStatusEmissionPie.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardServiceNow.operations.getDeviceStatusEmissionPie
						.endpoint,
				isMocked: false,
				mock: ServiceNow.getDeviceStatusEmissionPie
			},
			[ENDPOINT_GROUPS.dashboardServiceNow.operations.getDevicesModelsBar.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardServiceNow.operations.getDevicesModelsBar.endpoint,
				isMocked: false,
				mock: ServiceNow.getDevicesModelsBar
			},
			[ENDPOINT_GROUPS.dashboardServiceNow.operations.getDevicesModelsBarPerDevice.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardServiceNow.operations.getDevicesModelsBarPerDevice
						.endpoint,
				isMocked: false,
				mock: ServiceNow.getDevicesModelsBarPerDevice
			},
			[ENDPOINT_GROUPS.dashboardServiceNow.operations.getDevicesLocationBar.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardServiceNow.operations.getDevicesLocationBar.endpoint,
				isMocked: false,
				mock: ServiceNow.getDevicesLocationBar
			},
			[ENDPOINT_GROUPS.dashboardServiceNow.operations.getDevicesLocationBarPerDevice.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardServiceNow.operations.getDevicesLocationBarPerDevice
						.endpoint,
				isMocked: false,
				mock: ServiceNow.getDevicesLocationBarPerDevice
			},
			[ENDPOINT_GROUPS.dashboardServiceNow.operations.getTotalDevicesOverTime.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardServiceNow.operations.getTotalDevicesOverTime.endpoint,
				isMocked: false,
				mock: ServiceNow.getTotalDevicesOverTime
			},
			[ENDPOINT_GROUPS.dashboardServiceNow.operations.getLocationDevicesOverTime.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardServiceNow.operations.getLocationDevicesOverTime
						.endpoint,
				isMocked: false,
				mock: ServiceNow.getLocationDevicesOverTime
			},
			[ENDPOINT_GROUPS.dashboardServiceNow.operations.getLifecycleSavingsDevicesOverTime
				.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardServiceNow.operations
						.getLifecycleSavingsDevicesOverTime.endpoint,
				isMocked: false,
				mock: ServiceNow.getLifecycleSavingsDevicesOverTime
			},
			[ENDPOINT_GROUPS.dashboardServiceNow.operations.getManufacturerEmissionTable.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardServiceNow.operations.getManufacturerEmissionTable
						.endpoint,
				isMocked: false,
				mock: ServiceNow.getManufacturerEmissionTable
			}
		},
		[ENDPOINT_GROUPS.anomalyDetection.name]: {
			[ENDPOINT_GROUPS.anomalyDetection.operations.getAllAnomalousApplications.name]: {
				endpoint:
					ENDPOINT_GROUPS.anomalyDetection.operations.getAllAnomalousApplications
						.endpoint,
				isMocked: false,
				mock: AnomalyDetection.getAllAnomalousApplications
			},
			[ENDPOINT_GROUPS.anomalyDetection.operations.getAnomaliesByApplication.name]: {
				endpoint:
					ENDPOINT_GROUPS.anomalyDetection.operations.getAnomaliesByApplication.endpoint,
				isMocked: false,
				mock: AnomalyDetection.getAnomaliesByApplication
			},
			[ENDPOINT_GROUPS.anomalyDetection.operations.addUserDeviceByTenant.name]: {
				endpoint:
					ENDPOINT_GROUPS.anomalyDetection.operations.addUserDeviceByTenant.endpoint,
				isMocked: false,
				mock: AnomalyDetection.addUserDeviceByTenant
			},
			[ENDPOINT_GROUPS.anomalyDetection.operations.updateUserNotificationSettings.name]: {
				endpoint:
					ENDPOINT_GROUPS.anomalyDetection.operations.updateUserNotificationSettings
						.endpoint,
				isMocked: false,
				mock: AnomalyDetection.updateUserNotificationSettings
			}
		},
		[ENDPOINT_GROUPS.dashboardCio.name]: {
			[ENDPOINT_GROUPS.dashboardCio.operations.getSummary.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardCio.operations.getSummary.endpoint,
				isMocked: false,
				mock: DashboardCio.getSummary
			},
			[ENDPOINT_GROUPS.dashboardCio.operations.getForecast.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardCio.operations.getForecast.endpoint,
				isMocked: false,
				mock: DashboardCio.getForecast
			},
			[ENDPOINT_GROUPS.dashboardCio.operations.getEmissionsShare.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardCio.operations.getEmissionsShare.endpoint,
				isMocked: false,
				mock: DashboardCio.getEmissionsShare
			},
			[ENDPOINT_GROUPS.dashboardCio.operations.getDetails.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardCio.operations.getDetails.endpoint,
				isMocked: false,
				mock: DashboardCio.getDetails
			}
		},
		[ENDPOINT_GROUPS.dashboardHardwareAssetsRealtime.name]: {
			[ENDPOINT_GROUPS.dashboardHardwareAssetsRealtime.operations.getInformations.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssetsRealtime.operations.getInformations
						.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssetsRealtime.getInformations
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssetsRealtime.operations.getTotalEmissions.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssetsRealtime.operations.getTotalEmissions
						.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssetsRealtime.getTotalEmissions
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssetsRealtime.operations.getEmissionsByModelTotal
				.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssetsRealtime.operations
						.getEmissionsByModelTotal.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssetsRealtime.getEmissionsByModelTotal
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssetsRealtime.operations.getEmissionsByLocation
				.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssetsRealtime.operations
						.getEmissionsByLocation.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssetsRealtime.getEmissionsByLocation
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssetsRealtime.operations.getEmissionsByModelPerDevice
				.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssetsRealtime.operations
						.getEmissionsByModelPerDevice.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssetsRealtime.getEmissionsByModelPerDevice
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssetsRealtime.operations.getManufacturersEmission
				.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssetsRealtime.operations
						.getManufacturersEmission.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssetsRealtime.getManufacturersEmission
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssetsRealtime.operations.getManufacturerPieChart
				.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssetsRealtime.operations
						.getManufacturerPieChart.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssetsRealtime.getManufacturerPieChart
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssetsRealtime.operations.getCarbonIntensityMap.name]:
				{
					endpoint:
						ENDPOINT_GROUPS.dashboardHardwareAssetsRealtime.operations
							.getCarbonIntensityMap.endpoint,
					isMocked: false,
					mock: DashboardHardwareAssetsRealtime.getCarbonIntensityMap
				},
			[ENDPOINT_GROUPS.dashboardHardwareAssetsRealtime.operations.getEnergyConsumption.name]:
				{
					endpoint:
						ENDPOINT_GROUPS.dashboardHardwareAssetsRealtime.operations
							.getEnergyConsumption.endpoint,
					isMocked: false,
					mock: DashboardHardwareAssetsRealtime.getEnergyConsumption
				},
			[ENDPOINT_GROUPS.dashboardHardwareAssetsRealtime.operations.getEmbodiedEmissions.name]:
				{
					endpoint:
						ENDPOINT_GROUPS.dashboardHardwareAssetsRealtime.operations
							.getEmbodiedEmissions.endpoint,
					isMocked: false,
					mock: DashboardHardwareAssetsRealtime.getEmbodiedEmissions
				}
		},
		[ENDPOINT_GROUPS.dashboardConfig.name]: {
			[ENDPOINT_GROUPS.dashboardConfig.operations.getConfigurationByTenant.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardConfig.operations.getConfigurationByTenant.endpoint,
				isMocked: false,
				mock: DashboardConfig.getConfigurationByTenant
			}
		},
		[ENDPOINT_GROUPS.dashboardEnergy.name]: {
			[ENDPOINT_GROUPS.dashboardEnergy.operations.getAppsPerformance.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardEnergy.operations.getAppsPerformance.endpoint,
				isMocked: false,
				mock: DashboardEnergy.getAppsPerformance
			},
			[ENDPOINT_GROUPS.dashboardEnergy.operations.getTotalCo2.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardEnergy.operations.getTotalCo2.endpoint,
				isMocked: false,
				mock: DashboardEnergy.getTotalCo2
			},
			[ENDPOINT_GROUPS.dashboardEnergy.operations.getWorst10Apps.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardEnergy.operations.getWorst10Apps.endpoint,
				isMocked: false,
				mock: DashboardEnergy.getWorst10Apps
			},
			[ENDPOINT_GROUPS.dashboardEnergy.operations.getWorst10Layers.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardEnergy.operations.getWorst10Layers.endpoint,
				isMocked: false,
				mock: DashboardEnergy.getWorst10Layers
			}
		},
		[ENDPOINT_GROUPS.dashboardSci.name]: {
			[ENDPOINT_GROUPS.dashboardSci.operations.getGreenScore.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardSci.operations.getGreenScore.endpoint,
				isMocked: false,
				mock: DashboardSci.getGreenScore
			},
			[ENDPOINT_GROUPS.dashboardSci.operations.getWorst10Emissions.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardSci.operations.getWorst10Emissions.endpoint,
				isMocked: false,
				mock: DashboardSci.getWorst10Emissions
			}
		},
		[ENDPOINT_GROUPS.dashboardSoftwareAssets.name]: {
			[ENDPOINT_GROUPS.dashboardSoftwareAssets.operations.getAppsPerformance.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardSoftwareAssets.operations.getAppsPerformance.endpoint,
				isMocked: false,
				mock: DashboardSoftwareAssets.getAppsPerformance
			},
			[ENDPOINT_GROUPS.dashboardSoftwareAssets.operations.getAppsWaterConsumption.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardSoftwareAssets.operations.getAppsWaterConsumption
						.endpoint,
				isMocked: false,
				mock: DashboardSoftwareAssets.getAppsWaterConsumption
			},
			[ENDPOINT_GROUPS.dashboardSoftwareAssets.operations.getTotalCo2.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardSoftwareAssets.operations.getTotalCo2.endpoint,
				isMocked: false,
				mock: DashboardSoftwareAssets.getTotalCo2
			},
			[ENDPOINT_GROUPS.dashboardSoftwareAssets.operations.getTotalEmissions.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardSoftwareAssets.operations.getTotalEmissions.endpoint,
				isMocked: false,
				mock: DashboardSoftwareAssets.getTotalEmissions
			},
			[ENDPOINT_GROUPS.dashboardSoftwareAssets.operations.exportCsv.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardSoftwareAssets.operations.exportCsv.endpoint,
				isMocked: false,
				mock: DashboardSoftwareAssets.exportCsv
			},
			[ENDPOINT_GROUPS.dashboardSoftwareAssets.operations.exportManifest.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardSoftwareAssets.operations.exportManifest.endpoint,
				isMocked: false,
				mock: DashboardSoftwareAssets.exportManifest
			},
			[ENDPOINT_GROUPS.dashboardSoftwareAssets.operations.exportInsightsReport.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardSoftwareAssets.operations.exportInsightsReport
						.endpoint,
				isMocked: false,
				mock: DashboardSoftwareAssets.exportReport
			},
			[ENDPOINT_GROUPS.dashboardSoftwareAssets.operations.getEnvironmentalImpactByApplication
				.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardSoftwareAssets.operations
						.getEnvironmentalImpactByApplication.endpoint,
				isMocked: false,
				mock: DashboardSoftwareAssets.getEnvironmentalImpactByApplication
			},
			[ENDPOINT_GROUPS.dashboardSoftwareAssets.operations.getApplicationsIds.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardSoftwareAssets.operations.getApplicationsIds.endpoint,
				isMocked: false,
				mock: DashboardSoftwareAssetsFilters.getApplicationsIds
			},
			[ENDPOINT_GROUPS.dashboardSoftwareAssets.operations.getLayerIds.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardSoftwareAssets.operations.getLayerIds.endpoint,
				isMocked: false,
				mock: DashboardSoftwareAssetsFilters.getLayerIds
			}
		},
		[ENDPOINT_GROUPS.dashboardDataCenter.name]: {
			[ENDPOINT_GROUPS.dashboardDataCenter.operations.getSiteList.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardDataCenter.operations.getSiteList.endpoint,
				isMocked: false,
				mock: DashboardDataCenter.getSiteList
			},
			[ENDPOINT_GROUPS.dashboardDataCenter.operations.getMetricOverview.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardDataCenter.operations.getMetricOverview.endpoint,
				isMocked: false,
				mock: DashboardDataCenter.getMetricOverview
			},
			[ENDPOINT_GROUPS.dashboardDataCenter.operations.getMetricData.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardDataCenter.operations.getMetricData.endpoint,
				isMocked: false,
				mock: DashboardDataCenter.getMetricData
			}
		},
		[ENDPOINT_GROUPS.dashboardComponentEmissions.name]: {
			[ENDPOINT_GROUPS.dashboardComponentEmissions.operations.getTotalCo2Emissions.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardComponentEmissions.operations.getTotalCo2Emissions
						.endpoint,
				isMocked: false,
				mock: DashboardComponentEmissions.getTotalCo2Emissions
			},
			[ENDPOINT_GROUPS.dashboardComponentEmissions.operations.getTop10Emissions.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardComponentEmissions.operations.getTop10Emissions
						.endpoint,
				isMocked: false,
				mock: DashboardComponentEmissions.getTop10Emissions
			},
			[ENDPOINT_GROUPS.dashboardComponentEmissions.operations.getResourcesEmissions.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardComponentEmissions.operations.getResourcesEmissions
						.endpoint,
				isMocked: false,
				mock: DashboardComponentEmissions.getResourcesEmissions
			}
		},
		[ENDPOINT_GROUPS.regions.name]: {
			[ENDPOINT_GROUPS.regions.operations.getCarbonIntensityByRegion.name]: {
				endpoint: ENDPOINT_GROUPS.regions.operations.getCarbonIntensityByRegion.endpoint,
				isMocked: false,
				mock: Regions.getCarbonIntensityByRegion
			},
			[ENDPOINT_GROUPS.regions.operations.getAverageCarbonIntensity.name]: {
				endpoint: ENDPOINT_GROUPS.regions.operations.getAverageCarbonIntensity.endpoint,
				isMocked: false,
				mock: Regions.getAverageCarbonIntensity
			}
		},
		[ENDPOINT_GROUPS.dashboardImpact.name]: {
			[ENDPOINT_GROUPS.dashboardImpact.operations.getWorst10Layers.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardImpact.operations.getWorst10Layers.endpoint,
				isMocked: false,
				mock: DashboardImpact.getWorst10Layers
			},
			[ENDPOINT_GROUPS.dashboardImpact.operations.getWorst10Apps.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardImpact.operations.getWorst10Apps.endpoint,
				isMocked: false,
				mock: DashboardImpact.getWorst10Apps
			},
			[ENDPOINT_GROUPS.dashboardImpact.operations.downloadAsCsv.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardImpact.operations.downloadAsCsv.endpoint,
				isMocked: false,
				mock: DashboardImpact.downloadAsCsv
			}
		},
		[ENDPOINT_GROUPS.dashboardHardwareAssets.name]: {
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.renameFile.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardHardwareAssets.operations.renameFile.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.renameFile
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.deleteFile.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardHardwareAssets.operations.deleteFile.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.deleteFile
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.uploadFile.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardHardwareAssets.operations.uploadFile.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.uploadFile
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.downloadAsCsv.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardHardwareAssets.operations.downloadAsCsv.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.downloadAsCsv
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getUploadingStatus.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getUploadingStatus.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getUploadingStatus
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.downloadTemplate.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.downloadTemplate.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.downloadTemplate
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getFiles.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getFiles.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getFiles
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getCategories.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getCategories.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getCategories
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getGeneralInformation.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getGeneralInformation
						.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getGeneralInformation
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getTotalEmissionForPieChart.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getTotalEmissionForPieChart
						.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getTotalEmissionForPieChart
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getEmissionsByModel.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getEmissionsByModel.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getEmissionsByModel
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getEmissionsByModelPerDevice.name]:
				{
					endpoint:
						ENDPOINT_GROUPS.dashboardHardwareAssets.operations
							.getEmissionsByModelPerDevice.endpoint,
					isMocked: false,
					mock: DashboardHardwareAssets.getEmissionsByModelPerDevice
				},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getEmissionsOverTime.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getEmissionsOverTime
						.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getEmissionsOverTime
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getSavedLifecycleEmissions.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getSavedLifecycleEmissions
						.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getSavedLifecycleEmissions
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getEmissionsByCountryOverTime.name]:
				{
					endpoint:
						ENDPOINT_GROUPS.dashboardHardwareAssets.operations
							.getEmissionsByCountryOverTime.endpoint,
					isMocked: false,
					mock: DashboardHardwareAssets.getEmissionsByCountryOverTime
				},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getEmissionsByRegion.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getEmissionsByRegion
						.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getEmissionsByRegion
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getEmissionsByRegionPerDevice.name]:
				{
					endpoint:
						ENDPOINT_GROUPS.dashboardHardwareAssets.operations
							.getEmissionsByRegionPerDevice.endpoint,
					isMocked: false,
					mock: DashboardHardwareAssets.getEmissionsByRegionPerDevice
				},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getCo2ByInstallStatus.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getCo2ByInstallStatus
						.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getCo2ByInstallStatus
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getManufacturersEmission.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getManufacturersEmission
						.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getManufacturersEmission
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getCarbonFootprint.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getCarbonFootprint.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getCarbonFootprint
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getCarbonFootprintByScope.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getCarbonFootprintByScope
						.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getCarbonFootprintByScope
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getManufacturers.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getManufacturers.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getManufacturers
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getAllCountries.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getAllCountries.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getAllCountries
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getAllManufacturers.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getAllManufacturers.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getAllManufacturers
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.compute.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardHardwareAssets.operations.compute.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.compute
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getGeneralCategories.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getGeneralCategories
						.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getGeneralCategories
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getSpecificCategories.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getSpecificCategories
						.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getSpecificCategories
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getTotalEmission.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getTotalEmission.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getTotalEmission
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getEmissionOverview.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getEmissionOverview.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getEmissionOverview
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getCalculationKpis.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getCalculationKpis.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getCalculationKpis
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getEmissionByScope.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getEmissionByScope.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getEmissionByScope
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getEmissionByManufacturer.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getEmissionByManufacturer
						.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getEmissionByManufacturer
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.saveCalculation.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.saveCalculation.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.saveCalculation
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.deleteCalculation.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.deleteCalculation.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.deleteCalculation
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getCalculationStatus.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getCalculationStatus
						.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getCalculationStatus
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getScenario.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getScenario.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getScenario
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getScenarioStatus.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getScenarioStatus.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getScenarioStatus
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.deleteScenario.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.deleteScenario.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.deleteScenario
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getEmissionOverviewForBarChart
				.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations
						.getEmissionOverviewForBarChart.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getEmissionOverviewForBarChart
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getTotalEmissionByCategory.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getTotalEmissionByCategory
						.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getTotalEmissionByCategory
			},
			[ENDPOINT_GROUPS.dashboardHardwareAssets.operations.getTotalEmissionByManufacturer
				.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardHardwareAssets.operations
						.getTotalEmissionByManufacturer.endpoint,
				isMocked: false,
				mock: DashboardHardwareAssets.getTotalEmissionByManufacturer
			}
		},
		[ENDPOINT_GROUPS.dashboardDataCenters.name]: {
			[ENDPOINT_GROUPS.dashboardDataCenters.operations.getUserSiteList.name]: {
				endpoint: ENDPOINT_GROUPS.dashboardDataCenters.operations.getUserSiteList.endpoint,
				isMocked: false,
				mock: DashboardDataCenters.getUserSiteList
			},
			[ENDPOINT_GROUPS.dashboardDataCenters.operations.getMetricOverview.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardDataCenters.operations.getMetricOverview.endpoint,
				isMocked: false,
				mock: DashboardDataCenters.getMetricOverview
			},
			[ENDPOINT_GROUPS.dashboardDataCenters.operations.getUserSiteMetricData.name]: {
				endpoint:
					ENDPOINT_GROUPS.dashboardDataCenters.operations.getUserSiteMetricData.endpoint,
				isMocked: false,
				mock: DashboardDataCenters.getUserSiteMetricData
			}
		},
		[ENDPOINT_GROUPS.ai.name]: {
			[ENDPOINT_GROUPS.ai.operations.getFiles.name]: {
				endpoint: ENDPOINT_GROUPS.ai.operations.getFiles.endpoint,
				isMocked: false,
				mock: DashboardAI.getFiles
			},
			[ENDPOINT_GROUPS.ai.operations.uploadFile.name]: {
				endpoint: ENDPOINT_GROUPS.ai.operations.uploadFile.endpoint,
				isMocked: false,
				mock: DashboardAI.uploadFile
			},
			[ENDPOINT_GROUPS.ai.operations.getFileStatus.name]: {
				endpoint: ENDPOINT_GROUPS.ai.operations.getFileStatus.endpoint,
				isMocked: false,
				mock: DashboardAI.getFileStatus
			},
			[ENDPOINT_GROUPS.ai.operations.getFileEmissions.name]: {
				endpoint: ENDPOINT_GROUPS.ai.operations.getFileEmissions.endpoint,
				isMocked: false,
				mock: DashboardAI.getFileEmissions
			},
			[ENDPOINT_GROUPS.ai.operations.getGeneralInformation.name]: {
				endpoint: ENDPOINT_GROUPS.ai.operations.getGeneralInformation.endpoint,
				isMocked: false,
				mock: DashboardAI.getGeneralInformation
			},
			[ENDPOINT_GROUPS.ai.operations.getCo2AppsPerformance.name]: {
				endpoint: ENDPOINT_GROUPS.ai.operations.getCo2AppsPerformance.endpoint,
				isMocked: false,
				mock: DashboardAI.getCo2AppsPerformance
			},
			[ENDPOINT_GROUPS.ai.operations.getWaterAppsPerformance.name]: {
				endpoint: ENDPOINT_GROUPS.ai.operations.getWaterAppsPerformance.endpoint,
				isMocked: false,
				mock: DashboardAI.getWaterAppsPerformance
			},
			[ENDPOINT_GROUPS.ai.operations.getEnvironmentalImpactByCompany.name]: {
				endpoint: ENDPOINT_GROUPS.ai.operations.getEnvironmentalImpactByCompany.endpoint,
				isMocked: false,
				mock: DashboardAI.getEnvironmentalImpactByCompany
			},
			[ENDPOINT_GROUPS.ai.operations.getEnvironmentalImpactByModel.name]: {
				endpoint: ENDPOINT_GROUPS.ai.operations.getEnvironmentalImpactByModel.endpoint,
				isMocked: false,
				mock: DashboardAI.getEnvironmentalImpactByModel
			},
			[ENDPOINT_GROUPS.ai.operations.getWaterUsageByModel.name]: {
				endpoint: ENDPOINT_GROUPS.ai.operations.getWaterUsageByModel.endpoint,
				isMocked: false,
				mock: DashboardAI.getWaterUsageByModel
			},
			[ENDPOINT_GROUPS.ai.operations.getBest10ModelsByEmission.name]: {
				endpoint: ENDPOINT_GROUPS.ai.operations.getBest10ModelsByEmission.endpoint,
				isMocked: false,
				mock: DashboardAI.getBest10ModelsByEmission
			},
			[ENDPOINT_GROUPS.ai.operations.getBest10ModelsByWaterUsage.name]: {
				endpoint: ENDPOINT_GROUPS.ai.operations.getBest10ModelsByWaterUsage.endpoint,
				isMocked: false,
				mock: DashboardAI.getBest10ModelsByWaterUsage
			},
			[ENDPOINT_GROUPS.ai.operations.getModelEmissions.name]: {
				endpoint: ENDPOINT_GROUPS.ai.operations.getModelEmissions.endpoint,
				isMocked: false,
				mock: DashboardAI.getModelEmissions
			},
			[ENDPOINT_GROUPS.ai.operations.renameFile.name]: {
				endpoint: ENDPOINT_GROUPS.ai.operations.renameFile.endpoint,
				isMocked: false,
				mock: DashboardAI.renameFile
			},
			[ENDPOINT_GROUPS.ai.operations.deleteFile.name]: {
				endpoint: ENDPOINT_GROUPS.ai.operations.deleteFile.endpoint,
				isMocked: false,
				mock: DashboardAI.deleteFile
			},
			[ENDPOINT_GROUPS.ai.operations.exportFile.name]: {
				endpoint: ENDPOINT_GROUPS.ai.operations.exportFile.endpoint,
				isMocked: false,
				mock: DashboardAI.exportFile
			},
			[ENDPOINT_GROUPS.ai.operations.downloadTemplate.name]: {
				endpoint: ENDPOINT_GROUPS.ai.operations.downloadTemplate.endpoint,
				isMocked: false,
				mock: DashboardAI.downloadTemplate
			}
		},
		[ENDPOINT_GROUPS.whatIf.name]: {
			[ENDPOINT_GROUPS.whatIf.operations.getUserWhatIfData.name]: {
				endpoint: ENDPOINT_GROUPS.whatIf.operations.getUserWhatIfData.endpoint,
				isMocked: false,
				mock: WhatIf.getUserWhatIfData
			}
		},
		[ENDPOINT_GROUPS.whatIfScenario.name]: {
			[ENDPOINT_GROUPS.whatIfScenario.operations.createNewScenario.name]: {
				endpoint: ENDPOINT_GROUPS.whatIfScenario.operations.createNewScenario.endpoint,
				isMocked: false,
				mock: WhatIfScenario.createNewScenario
			},
			[ENDPOINT_GROUPS.whatIfScenario.operations.getScenarioInfo.name]: {
				endpoint: ENDPOINT_GROUPS.whatIfScenario.operations.getScenarioInfo.endpoint,
				isMocked: false,
				mock: WhatIfScenario.getScenarioInfo
			},
			[ENDPOINT_GROUPS.whatIfScenario.operations.deleteScenario.name]: {
				endpoint: ENDPOINT_GROUPS.whatIfScenario.operations.deleteScenario.endpoint,
				isMocked: false,
				mock: WhatIfScenario.deleteScenario
			},
			[ENDPOINT_GROUPS.whatIfScenario.operations.getScenarioSimulationStatus.name]: {
				endpoint:
					ENDPOINT_GROUPS.whatIfScenario.operations.getScenarioSimulationStatus.endpoint,
				isMocked: false,
				mock: WhatIfScenario.getScenarioSimulationStatus
			}
		},
		[ENDPOINT_GROUPS.whatIfCarbonIntensity.name]: {
			[ENDPOINT_GROUPS.whatIfCarbonIntensity.operations.getAllCarbonIntensity.name]: {
				endpoint:
					ENDPOINT_GROUPS.whatIfCarbonIntensity.operations.getAllCarbonIntensity.endpoint,
				isMocked: false,
				mock: WhatIfCarbonIntensity.getAllCarbonIntensity
			},
			[ENDPOINT_GROUPS.whatIfCarbonIntensity.operations.getMonthlyAverageByRegion.name]: {
				endpoint:
					ENDPOINT_GROUPS.whatIfCarbonIntensity.operations.getMonthlyAverageByRegion
						.endpoint,
				isMocked: false,
				mock: WhatIfCarbonIntensity.getMonthlyAverageByRegion
			},
			[ENDPOINT_GROUPS.whatIfCarbonIntensity.operations.getHourlyAverageByRegion.name]: {
				endpoint:
					ENDPOINT_GROUPS.whatIfCarbonIntensity.operations.getHourlyAverageByRegion
						.endpoint,
				isMocked: false,
				mock: WhatIfCarbonIntensity.getHourlyAverageByRegion
			}
		},
		[ENDPOINT_GROUPS.whatIfLayer.name]: {
			[ENDPOINT_GROUPS.whatIfLayer.operations.editAllScenarioLayers.name]: {
				endpoint: ENDPOINT_GROUPS.whatIfLayer.operations.editAllScenarioLayers.endpoint,
				isMocked: false,
				mock: WhatIfLayer.editAllScenarioLayers
			},
			[ENDPOINT_GROUPS.whatIfLayer.operations.editScenarioLayer.name]: {
				endpoint: ENDPOINT_GROUPS.whatIfLayer.operations.editScenarioLayer.endpoint,
				isMocked: false,
				mock: WhatIfLayer.editScenarioLayer
			}
		},
		[ENDPOINT_GROUPS.whatIfSimulation.name]: {
			[ENDPOINT_GROUPS.whatIfSimulation.operations.runSimulation.name]: {
				endpoint: ENDPOINT_GROUPS.whatIfSimulation.operations.runSimulation.endpoint,
				isMocked: false,
				mock: WhatIfSimulation.runSimulation
			}
		},
		[ENDPOINT_GROUPS.calculator.name]: {
			[ENDPOINT_GROUPS.calculator.operations.getCalculatorFormData.name]: {
				endpoint: ENDPOINT_GROUPS.calculator.operations.getCalculatorFormData.endpoint,
				isMocked: false,
				mock: Calculator.getCalculatorFormData
			},
			[ENDPOINT_GROUPS.calculator.operations.getOnPremiseOptions.name]: {
				endpoint: ENDPOINT_GROUPS.calculator.operations.getOnPremiseOptions.endpoint,
				isMocked: false,
				mock: Calculator.getOnPremiseOptions
			},
			[ENDPOINT_GROUPS.calculator.operations.getCloudProviderOptions.name]: {
				endpoint: ENDPOINT_GROUPS.calculator.operations.getCloudProviderOptions.endpoint,
				isMocked: false,
				mock: Calculator.getCloudProviderOptions
			},
			[ENDPOINT_GROUPS.calculator.operations.startCalculator.name]: {
				endpoint: ENDPOINT_GROUPS.calculator.operations.startCalculator.endpoint,
				isMocked: false,
				mock: Calculator.startCalculator
			}
		},
		[ENDPOINT_GROUPS.cloud.name]: {
			[ENDPOINT_GROUPS.cloud.operations.getCloudFilters.name]: {
				endpoint: ENDPOINT_GROUPS.cloud.operations.getCloudFilters.endpoint,
				isMocked: false,
				mock: DashboardCloud.getCloudFilters
			},
			[ENDPOINT_GROUPS.cloud.operations.getCloudPerformance.name]: {
				endpoint: ENDPOINT_GROUPS.cloud.operations.getCloudPerformance.endpoint,
				isMocked: false,
				mock: DashboardCloud.getCloudPerformance
			},
			[ENDPOINT_GROUPS.cloud.operations.getTotalEmissionForPieChart.name]: {
				endpoint: ENDPOINT_GROUPS.cloud.operations.getTotalEmissionForPieChart.endpoint,
				isMocked: false,
				mock: DashboardCloud.getCloudEmissionsForPieChart
			},
			[ENDPOINT_GROUPS.cloud.operations.getTotalCo2.name]: {
				endpoint: ENDPOINT_GROUPS.cloud.operations.getTotalCo2.endpoint,
				isMocked: false,
				mock: DashboardCloud.getTotalCo2
			},
			[ENDPOINT_GROUPS.cloud.operations.getWorst10Applications.name]: {
				endpoint: ENDPOINT_GROUPS.cloud.operations.getWorst10Applications.endpoint,
				isMocked: false,
				mock: DashboardCloud.getWorst10Applications
			},
			[ENDPOINT_GROUPS.cloud.operations.getWorst10Locations.name]: {
				endpoint: ENDPOINT_GROUPS.cloud.operations.getWorst10Locations.endpoint,
				isMocked: false,
				mock: DashboardCloud.getWorst10Locations
			},
			[ENDPOINT_GROUPS.cloud.operations.getEmissionsAnalysis.name]: {
				endpoint: ENDPOINT_GROUPS.cloud.operations.getEmissionsAnalysis.endpoint,
				isMocked: false,
				mock: DashboardCloud.getEmissionsAnalysis
			},
			[ENDPOINT_GROUPS.cloud.operations.getRegionsEmissions.name]: {
				endpoint: ENDPOINT_GROUPS.cloud.operations.getRegionsEmissions.endpoint,
				isMocked: false,
				mock: DashboardCloud.getRegionsEmissions
			},
			[ENDPOINT_GROUPS.cloud.operations.exportReport.name]: {
				endpoint: ENDPOINT_GROUPS.cloud.operations.exportReport.endpoint,
				isMocked: false,
				mock: DashboardCloud.exportReport
			},
			[ENDPOINT_GROUPS.cloud.operations.exportCsv.name]: {
				endpoint: ENDPOINT_GROUPS.cloud.operations.exportCsv.endpoint,
				isMocked: false,
				mock: DashboardCloud.exportCsv
			}
		}
	}
};
